import { inject, Service } from '@angular/core';
import type {
  OnyxDropdownOptionsSourceResult,
  OnyxOption,
} from '@onyx/angular';
import type { Observable } from 'rxjs';
import { map, startWith, Subject, switchMap, tap } from 'rxjs';
import { ApiService } from '../../../../../common/services/api.service';
import { CacheService } from '../../../../../common/services/cache.service';
import { BasesStorageKey } from '../enums/bases-storage-key';
import type { BaseFormDto } from '../helpers/base-form.helper';
import type { Base } from '../interfaces/base';

@Service()
export class BasesService extends ApiService {
  private readonly cacheService = inject(CacheService);

  private _reload$ = new Subject<void>();
  public get reload$() {
    return this._reload$.asObservable();
  }

  constructor() {
    super();
  }

  public listBases(): Observable<Base[]> {
    return this.cacheService.cacheRequest(
      BasesStorageKey.LIST_BASES,
      this.get<Base[]>('/bases'),
      this.reload$,
    );
  }

  public searchBases(
    query: string,
  ): Observable<OnyxDropdownOptionsSourceResult<Base>> {
    return this.reload$.pipe(
      startWith(undefined),
      switchMap(() => this.get<Base[]>('/bases', { params: { query } })),
      map((response) => ({
        options: response.map((base): OnyxOption<Base> => ({
          name: base.name,
          value: base,
          leftFlag: base.address.countryCode,
        })),
        totalItems: response.length,
      })),
    );
  }

  public getBase(uuid: string): Observable<Base> {
    return this.get(`/bases/${uuid}`);
  }

  public addBase(dto: BaseFormDto): Observable<Base> {
    return this.post<Base>('/bases', dto).pipe(tap(() => this._reload$.next()));
  }

  public editBase(uuid: string, dto: BaseFormDto): Observable<Base> {
    return this.put<Base>(`/bases/${uuid}`, dto).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public deleteBase(uuid: string): Observable<void> {
    return this.delete<void>(`/bases/${uuid}`).pipe(
      tap(() => this._reload$.next()),
    );
  }
}
